import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Clock, Users, Phone, Mail, Check, X, Table2,
  Plus, Trash2, RefreshCw, ArrowLeft, LogOut, Lock, Eye, EyeOff,
  MapPin, ChevronRight, ShoppingBag, Package, Bell, Pencil, BookOpen, Clock4
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { logger } from '../lib/logger';
import { playOrderAlertLoop } from '../lib/audioAlert';
import { fullMenu } from '../data/mock';

const MENU_CATEGORY_LABELS = {
  pizza: 'Pizza',
  pasta: 'Pasta',
  salads: 'Salads',
  risotto: 'Risotto',
  antipasti: 'Antipasti',
  tradizionaliItaliani: 'Tradizionali Italiani',
  caldiFritti: 'Caldi & Fritti',
  meatAndFish: 'Meat & Fish',
  kidsMenu: 'Kids Menu',
  desserts: 'Desserts',
  softDrinks: 'Soft Drinks',
  beers: 'Beers',
  redWines: 'Red Wines',
  whiteWines: 'White Wines',
  roseWines: 'Rosé Wines',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Floor plan image URL
const FLOOR_PLAN_IMAGE = "/images/floorplan.png";

// Approximate {x%, y%} of each table on the floor plan image
const FLOOR_PLAN_POSITIONS = {
  'table-1':  { x: 38, y: 78 },
  'table-2':  { x: 23, y: 45 },
  'table-3':  { x: 23, y: 26 },
  'table-4':  { x: 23, y: 8 },
  'table-5':  { x: 47, y: 45 },
  'table-6':  { x: 47, y: 26 },
  'table-7':  { x: 47, y: 8 },
  'table-8':  { x: 71, y: 45 },
  'table-9':  { x: 71, y: 26 },
  'table-10': { x: 71, y: 8 },
  'table-11': { x: 89, y: 8 },
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(() => new Date().toISOString().slice(0, 10));
  const [newTable, setNewTable] = useState({ name: '', capacity: '', location: '' });
  const [isAddTableOpen, setIsAddTableOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null); // table object being edited or null
  const [editingBooking, setEditingBooking] = useState(null); // booking object being edited or null
  const [tableViewModal, setTableViewModal] = useState(null); // { table, bookings } when a floor plan marker is clicked
  const [orderPause, setOrderPause] = useState({ collection_paused: false, delivery_paused: false, pause_message: '', blocks: [] });
  const [newOrderBlock, setNewOrderBlock] = useState({ date: '', start: '12:00', end: '17:00', reason: '' });
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isAssignTableOpen, setIsAssignTableOpen] = useState(false);
  const [selectedTableForView, setSelectedTableForView] = useState(null);
  const [tableBookings, setTableBookings] = useState([]);
  const [isAddBookingOpen, setIsAddBookingOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');  const knownOrderIds = useRef(new Set());
  const [showTodayOrdersOnly, setShowTodayOrdersOnly] = useState(true);
  const isFirstLoad = useRef(true);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioControllerRef = useRef(null);
  // Menu stock: Map<"category::name", boolean in_stock> — missing keys default to in_stock=true
  const [menuStock, setMenuStock] = useState({});
  const [menuStockLoading, setMenuStockLoading] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  // Ordering hours editor
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [hoursDraft, setHoursDraft] = useState(null); // { collection: [...7], delivery: [...7] } each entry [oh,om,ch,cm] or null
  const [hoursDefaults, setHoursDefaults] = useState(null);
  const [hoursSaving, setHoursSaving] = useState(false);
  // Bookings pause (master "Close Bookings" switch + scheduled block windows)
  const [bookingsPause, setBookingsPause] = useState({ closed: false, message: '', blocks: [] });
  const [bookingsPauseDialogOpen, setBookingsPauseDialogOpen] = useState(false);
  const [newBlock, setNewBlock] = useState({ date: '', start: '18:00', end: '22:00', reason: '' });
  const [bulkRange, setBulkRange] = useState({ from: '', to: '', start: '12:00', end: '17:00', reason: '' });
  const [newBooking, setNewBooking] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    special_requests: '',
    table_id: '',
    table_ids: []
  });

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthToken(token);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('admin_token');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setAuthToken(response.data.token);
        setIsAuthenticated(true);
        toast.success('Login successful!');
      }
    } catch (error) {
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (error) {
      logger.error('Logout error:', error);
    }
    localStorage.removeItem('admin_token');
    setAuthToken(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      logger.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      setTables(response.data);
    } catch (error) {
      logger.error('Error fetching tables:', error);
    }
  };

  const fetchTableBookings = async (tableId) => {
    try {
      const response = await axios.get(`${API}/tables/${tableId}/bookings`);
      setTableBookings(response.data);
    } catch (error) {
      logger.error('Error fetching table bookings:', error);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);
      const response = await axios.get(`${API}/orders`);
      const fetchedOrders = response.data;

      // Detect new orders (skip on first load)
      if (!isFirstLoad.current) {
        const newOrders = fetchedOrders.filter(o => !knownOrderIds.current.has(o.id));
        if (newOrders.length > 0) {
          setNewOrderAlert(newOrders[newOrders.length - 1]);
        }
      }

      // Update known IDs
      knownOrderIds.current = new Set(fetchedOrders.map(o => o.id));
      isFirstLoad.current = false;

      setOrders(fetchedOrders);
    } catch (error) {
      logger.error('Error fetching orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const fetchMenuStock = useCallback(async () => {
    setMenuStockLoading(true);
    try {
      const response = await axios.get(`${API}/menu/stock`);
      const map = {};
      (response.data?.items || []).forEach(it => {
        if (it.key) map[it.key] = !!it.in_stock;
      });
      setMenuStock(map);
    } catch (error) {
      logger.error('Error fetching menu stock:', error);
    } finally {
      setMenuStockLoading(false);
    }
  }, []);

  const toggleMenuStock = async (category, name, currentlyInStock) => {
    const key = `${category}::${name}`;
    const newInStock = !currentlyInStock;
    // Optimistic update
    setMenuStock(prev => ({ ...prev, [key]: newInStock }));
    try {
      await axios.put(`${API}/menu/stock`, { category, name, in_stock: newInStock });
      toast.success(`${name} marked ${newInStock ? 'In Stock' : 'Sold Out'}`);
    } catch (error) {
      // Revert
      setMenuStock(prev => ({ ...prev, [key]: currentlyInStock }));
      logger.error('Error updating menu stock:', error);
      toast.error('Failed to update stock. Please try again.');
    }
  };

  const fetchHours = useCallback(async (open = false) => {
    try {
      const res = await axios.get(`${API}/orders/hours`);
      setHoursDraft({
        collection: res.data.collection,
        delivery: res.data.delivery,
      });
      setHoursDefaults(res.data.defaults || null);
      if (open) setHoursDialogOpen(true);
    } catch (error) {
      logger.error('Error fetching hours:', error);
      toast.error('Failed to load ordering hours');
    }
  }, []);

  const saveHours = async () => {
    if (!hoursDraft) return;
    setHoursSaving(true);
    try {
      await axios.put(`${API}/orders/hours`, hoursDraft);
      toast.success('Ordering hours updated — live on the website now');
      setHoursDialogOpen(false);
    } catch (error) {
      logger.error('Error saving hours:', error);
      toast.error(error.response?.data?.detail || 'Failed to save hours');
    } finally {
      setHoursSaving(false);
    }
  };

  const resetHoursToDefaults = async () => {
    setHoursSaving(true);
    try {
      const res = await axios.delete(`${API}/orders/hours`);
      setHoursDraft({ collection: res.data.collection, delivery: res.data.delivery });
      toast.success('Reset to standard hours');
    } catch (error) {
      logger.error('Error resetting hours:', error);
      toast.error('Failed to reset hours');
    } finally {
      setHoursSaving(false);
    }
  };

  const fetchBookingsPause = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings/status`);
      setBookingsPause({
        closed: !!res.data.closed,
        message: res.data.message || '',
        blocks: Array.isArray(res.data.blocks) ? res.data.blocks : [],
      });
    } catch (error) {
      logger.error('Error fetching bookings pause:', error);
    }
  }, []);

  const saveBookingsPause = async (next) => {
    try {
      const res = await axios.put(`${API}/bookings/pause`, next);
      setBookingsPause({
        closed: !!res.data.closed,
        message: res.data.message || '',
        blocks: Array.isArray(res.data.blocks) ? res.data.blocks : [],
      });
      toast.success(next.closed ? 'Online table bookings are now CLOSED on the website.' : 'Bookings settings saved.');
    } catch (error) {
      logger.error('Error saving bookings pause:', error);
      toast.error(error.response?.data?.detail || 'Failed to update bookings status');
    }
  };

  const addBlockToBookings = async () => {
    if (!newBlock.date || !newBlock.start || !newBlock.end) {
      toast.error('Date, start and end are required');
      return;
    }
    if (newBlock.start >= newBlock.end) {
      toast.error('Start time must be before end time');
      return;
    }
    const next = {
      ...bookingsPause,
      blocks: [...bookingsPause.blocks, newBlock],
    };
    await saveBookingsPause(next);
    setNewBlock({ date: '', start: '18:00', end: '22:00', reason: '' });
  };

  const removeBlock = async (blockId) => {
    const next = {
      ...bookingsPause,
      blocks: bookingsPause.blocks.filter(b => b.id !== blockId),
    };
    await saveBookingsPause(next);
  };

  // Build a list of daily blocks between two dates (inclusive)
  const buildDateRangeBlocks = (fromDate, toDate, start, end, reason) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    if (isNaN(from) || isNaN(to) || from > to) return [];
    const out = [];
    const cursor = new Date(from);
    while (cursor <= to) {
      out.push({
        date: cursor.toISOString().slice(0, 10),
        start,
        end,
        reason: reason || '',
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  };

  const addBookingBulkRange = async (fromDate, toDate, start, end, reason) => {
    const generated = buildDateRangeBlocks(fromDate, toDate, start, end, reason);
    if (generated.length === 0) {
      toast.error('Invalid date range');
      return;
    }
    // Skip duplicates by (date+start+end)
    const seen = new Set(bookingsPause.blocks.map(b => `${b.date}|${b.start}|${b.end}`));
    const toAdd = generated.filter(b => !seen.has(`${b.date}|${b.start}|${b.end}`));
    if (toAdd.length === 0) {
      toast('All those dates are already blocked.');
      return;
    }
    const next = { ...bookingsPause, blocks: [...bookingsPause.blocks, ...toAdd] };
    await saveBookingsPause(next);
    toast.success(`Blocked ${toAdd.length} day${toAdd.length > 1 ? 's' : ''} for bookings.`);
  };

  const addOrderBulkRange = async (fromDate, toDate, start, end, reason) => {
    const generated = buildDateRangeBlocks(fromDate, toDate, start, end, reason);
    if (generated.length === 0) {
      toast.error('Invalid date range');
      return;
    }
    const seen = new Set(orderPause.blocks.map(b => `${b.date}|${b.start}|${b.end}`));
    const toAdd = generated.filter(b => !seen.has(`${b.date}|${b.start}|${b.end}`));
    if (toAdd.length === 0) {
      toast('All those dates are already blocked.');
      return;
    }
    const next = { ...orderPause, blocks: [...orderPause.blocks, ...toAdd] };
    await saveOrderPause(next);
    toast.success(`Blocked ${toAdd.length} day${toAdd.length > 1 ? 's' : ''} for orders.`);
  };

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      fetchTables();
      fetchOrders();
      fetchMenuStock();
      fetchHours(false);
      fetchBookingsPause();
    }
  }, [isAuthenticated, fetchOrders, fetchMenuStock, fetchHours, fetchBookingsPause]);

  // Poll for new orders every 15 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchOrders]);

  // Play loud audio alert when a new order arrives
  useEffect(() => {
    // Stop any in-flight alert
    if (audioControllerRef.current) {
      audioControllerRef.current.stop();
      audioControllerRef.current = null;
    }
    if (newOrderAlert && soundEnabled) {
      audioControllerRef.current = playOrderAlertLoop({ durationSec: 60 });
    }
    return () => {
      if (audioControllerRef.current) {
        audioControllerRef.current.stop();
        audioControllerRef.current = null;
      }
    };
  }, [newOrderAlert, soundEnabled]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status });
      toast.success(`Order ${status}!`);
      fetchOrders();
    } catch (error) {
      logger.error('Error updating order:', error);
      toast.error('Failed to update order status');
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'preparing': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'ready': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getPaymentStatusColor = (ps) => {
    if (ps === 'paid') return 'bg-green-100 text-green-700';
    if (ps === 'pay_in_store') return 'bg-blue-100 text-blue-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  // Order priority for sorting (lower = higher priority / shown at top)
  const ORDER_STATUS_PRIORITY = {
    pending: 0,
    confirmed: 1,
    preparing: 2,
    ready: 3,
    completed: 4,
    cancelled: 5,
  };

  const filteredOrders = orders
    .filter(o => {
      if (!showTodayOrdersOnly) return true;
      const todayUK = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' }); // YYYY-MM-DD
      const orderDateUK = new Date(o.created_at).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
      return orderDateUK === todayUK;
    })
    .filter(o => orderStatusFilter === 'all' || o.status === orderStatusFilter)
    .slice()
    .sort((a, b) => {
      // Active orders first
      const pa = ORDER_STATUS_PRIORITY[a.status] ?? 99;
      const pb = ORDER_STATUS_PRIORITY[b.status] ?? 99;
      if (pa !== pb) return pa - pb;
      // Within same status: newest first
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { status });
      toast.success(`Booking ${status}! Customer has been notified.`);
      fetchBookings();
    } catch (error) {
      logger.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const assignTable = async (bookingId, tableId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { table_id: tableId });
      toast.success('Table assigned successfully!');
      setIsAssignTableOpen(false);
      fetchBookings();
    } catch (error) {
      logger.error('Error assigning table:', error);
      toast.error('Failed to assign table');
    }
  };

  const createTable = async () => {
    if (!newTable.name || !newTable.capacity) {
      toast.error('Please fill in table name and capacity');
      return;
    }
    try {
      await axios.post(`${API}/tables`, {
        name: newTable.name,
        capacity: parseInt(newTable.capacity),
        location: newTable.location
      });
      toast.success('Table created successfully!');
      setNewTable({ name: '', capacity: '', location: '' });
      setIsAddTableOpen(false);
      fetchTables();
    } catch (error) {
      logger.error('Error creating table:', error);
      toast.error('Failed to create table');
    }
  };

  const deleteTable = async (tableId) => {
    try {
      await axios.delete(`${API}/tables/${tableId}`);
      toast.success('Table deleted');
      fetchTables();
    } catch (error) {
      logger.error('Error deleting table:', error);
      toast.error('Failed to delete table');
    }
  };

  const updateTable = async () => {
    if (!editingTable) return;
    if (!editingTable.name || !editingTable.capacity) {
      toast.error('Name and capacity are required');
      return;
    }
    try {
      await axios.put(`${API}/tables/${editingTable.id}`, {
        name: editingTable.name,
        capacity: parseInt(editingTable.capacity, 10),
        location: editingTable.location || '',
      });
      toast.success(`${editingTable.name} updated`);
      setEditingTable(null);
      fetchTables();
    } catch (error) {
      logger.error('Error updating table:', error);
      toast.error('Failed to update table');
    }
  };

  const cleanupOldBookings = async () => {
    if (!window.confirm('Remove all bookings with a date before today? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/bookings/cleanup-old`);
      toast.success(`Removed ${res.data.deleted} old booking(s)`);
      fetchBookings();
    } catch (error) {
      logger.error('Error cleaning bookings:', error);
      toast.error('Failed to clean up old bookings');
    }
  };

  const saveBookingEdit = async () => {
    if (!editingBooking) return;
    if (!editingBooking.name || !editingBooking.date || !editingBooking.time || !editingBooking.guests) {
      toast.error('Name, date, time and guests are required');
      return;
    }
    try {
      const tableIds = editingBooking.table_ids || [];
      await axios.put(`${API}/bookings/${editingBooking.id}`, {
        name: editingBooking.name,
        email: editingBooking.email || '',
        phone: editingBooking.phone || '',
        date: editingBooking.date,
        time: editingBooking.time,
        guests: String(editingBooking.guests),
        special_requests: editingBooking.special_requests || '',
        table_id: tableIds[0] || editingBooking.table_id || null,
        combined_table_ids: tableIds.length > 1 ? tableIds : null,
      });
      toast.success('Booking updated');
      setEditingBooking(null);
      fetchBookings();
    } catch (error) {
      logger.error('Error updating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success('Booking deleted');
      setEditingBooking(null);
      fetchBookings();
    } catch (error) {
      logger.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const fetchOrderPause = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/status`);
      setOrderPause({
        collection_paused: res.data.collection?.paused || false,
        delivery_paused: res.data.delivery?.paused || false,
        pause_message: res.data.pause_message || '',
        blocks: Array.isArray(res.data.blocks) ? res.data.blocks : [],
      });
    } catch (error) {
      logger.error('Error fetching pause state:', error);
    }
  }, []);

  const saveOrderPause = async (next) => {
    try {
      const res = await axios.put(`${API}/orders/pause`, next);
      setOrderPause({
        collection_paused: !!res.data.collection_paused,
        delivery_paused: !!res.data.delivery_paused,
        pause_message: res.data.pause_message || '',
        blocks: Array.isArray(res.data.blocks) ? res.data.blocks : [],
      });
      const states = [];
      if (next.collection_paused) states.push('Collection paused');
      if (next.delivery_paused) states.push('Delivery paused');
      toast.success(states.length ? states.join(' · ') : 'Ordering settings saved');
    } catch (error) {
      logger.error('Error updating pause state:', error);
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const addOrderBlock = async () => {
    if (!newOrderBlock.date || !newOrderBlock.start || !newOrderBlock.end) {
      toast.error('Date, start and end are required');
      return;
    }
    if (newOrderBlock.start >= newOrderBlock.end) {
      toast.error('Start time must be before end time');
      return;
    }
    const next = { ...orderPause, blocks: [...orderPause.blocks, newOrderBlock] };
    await saveOrderPause(next);
    setNewOrderBlock({ date: '', start: '12:00', end: '17:00', reason: '' });
  };

  const removeOrderBlock = async (blockId) => {
    const next = { ...orderPause, blocks: orderPause.blocks.filter(b => b.id !== blockId) };
    await saveOrderPause(next);
  };

  // Fetch ordering pause state on auth
  useEffect(() => {
    if (isAuthenticated) fetchOrderPause();
  }, [isAuthenticated, fetchOrderPause]);

  const createManualBooking = async () => {
    if (!newBooking.name || !newBooking.date || !newBooking.time || !newBooking.guests) {
      toast.error('Please fill in name, date, time, and number of guests');
      return;
    }
    try {
      const tableIds = newBooking.table_ids || [];
      await axios.post(`${API}/bookings/manual`, {
        name: newBooking.name,
        email: newBooking.email || '',
        phone: newBooking.phone || '',
        date: newBooking.date,
        time: newBooking.time,
        guests: newBooking.guests,
        special_requests: newBooking.special_requests || '',
        table_id: tableIds[0] || null,
        combined_table_ids: tableIds.length > 1 ? tableIds : null,
        status: 'accepted'
      });
      toast.success('Booking added successfully!');
      setNewBooking({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        guests: '',
        special_requests: '',
        table_id: '',
        table_ids: []
      });
      setIsAddBookingOpen(false);
      fetchBookings();
    } catch (error) {
      logger.error('Error creating booking:', error);
      toast.error('Failed to create booking');
    }
  };

  // Stats only count bookings within the selected date filter (status filter ignored)
  const dateFilteredBookings = bookings.filter(b => !dateFilter || b.date === dateFilter);
  const filteredBookings = dateFilteredBookings
    .filter(b => statusFilter === 'all' || b.status === statusFilter)
    .slice()
    .sort((a, b) => {
      // Earliest date+time first
      const da = `${a.date} ${a.time}`;
      const db = `${b.date} ${b.time}`;
      if (da !== db) return da.localeCompare(db);
      return new Date(a.created_at) - new Date(b.created_at);
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800 border-green-200';
      case 'declined': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTableBookingsForDate = (tableId, date) => {
    return bookings.filter(b => {
      const matchesTable = b.table_id === tableId || (Array.isArray(b.combined_table_ids) && b.combined_table_ids.includes(tableId));
      return matchesTable &&
        b.status === 'accepted' &&
        (!date || b.date === date);
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#1B4332] rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock size={28} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#1B4332]">Admin Login</h1>
              <p className="text-gray-600 mt-2">La Pasteria Pizza Bar</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  placeholder="Enter username"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[#1B4332] hover:bg-[#2D6A4F]"
              >
                {loginLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>

            <Link to="/" className="block text-center mt-6 text-gray-600 hover:text-[#1B4332]">
              ← Back to Website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1B4332] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Website</span>
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold ml-4 sm:ml-6">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Dialog open={isAddBookingOpen} onOpenChange={setIsAddBookingOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                  <Plus size={16} className="sm:mr-2" />
                  <span className="hidden sm:inline">Add Booking</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Manual Booking</DialogTitle>
                  <DialogDescription>
                    Create a new booking directly. No notification emails will be sent.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      placeholder="e.g., John Smith"
                      value={newBooking.name}
                      onChange={(e) => setNewBooking({ ...newBooking, name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={newBooking.date}
                        onChange={(e) => setNewBooking({ ...newBooking, date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Time *</Label>
                      <Input
                        type="time"
                        value={newBooking.time}
                        onChange={(e) => setNewBooking({ ...newBooking, time: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Number of Guests *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 4"
                      min="1"
                      value={newBooking.guests}
                      onChange={(e) => setNewBooking({ ...newBooking, guests: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      placeholder="e.g., +44 7123 456789"
                      value={newBooking.phone}
                      onChange={(e) => setNewBooking({ ...newBooking, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="e.g., john@example.com"
                      value={newBooking.email}
                      onChange={(e) => setNewBooking({ ...newBooking, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Assign Table(s)</Label>
                    <p className="text-xs text-gray-500 mb-2">Tick one or more tables. For larger parties select multiple to combine seats.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto border rounded-lg p-2">
                      {tables.map((table) => {
                        const checked = (newBooking.table_ids || []).includes(table.id);
                        return (
                          <label
                            key={table.id}
                            data-testid={`manual-booking-table-${table.id}`}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${checked ? 'bg-[#1B4332] text-white' : 'bg-white hover:bg-gray-50 border'}`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={checked}
                              onChange={(e) => {
                                const ids = newBooking.table_ids || [];
                                const next = e.target.checked
                                  ? [...ids, table.id]
                                  : ids.filter(id => id !== table.id);
                                setNewBooking({ ...newBooking, table_ids: next });
                              }}
                            />
                            <span className="font-semibold">{table.name.replace(/^Table\s*/i, 'T')}</span>
                            <span className="text-xs opacity-80">{table.capacity}p</span>
                          </label>
                        );
                      })}
                    </div>
                    {(newBooking.table_ids?.length > 1) && (
                      <p className="text-xs text-[#1B4332] mt-2 font-medium">
                        Combined capacity: {newBooking.table_ids.reduce((s, id) => s + (tables.find(t => t.id === id)?.capacity || 0), 0)} seats across {newBooking.table_ids.length} tables
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Special Requests / Notes</Label>
                    <Input
                      placeholder="e.g., Birthday celebration, wheelchair access"
                      value={newBooking.special_requests}
                      onChange={(e) => setNewBooking({ ...newBooking, special_requests: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddBookingOpen(false)}>Cancel</Button>
                  <Button onClick={createManualBooking} className="bg-[#1B4332] hover:bg-[#2D6A4F]">Add Booking</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={fetchBookings} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
              <RefreshCw size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              data-testid="toggle-sound-btn"
              onClick={() => {
                if (soundEnabled) {
                  if (audioControllerRef.current) {
                    audioControllerRef.current.stop();
                    audioControllerRef.current = null;
                  }
                  toast('Order alert sound muted');
                } else {
                  // Test the sound briefly so user hears it works
                  const t = playOrderAlertLoop();
                  setTimeout(() => t.stop(), 1200);
                  toast.success('Order alert sound enabled');
                }
                setSoundEnabled(prev => !prev);
              }}
              variant="outline"
              size="sm"
              className={`border-white/30 text-white hover:bg-white/10 ${soundEnabled ? 'bg-[#C67C4E]/40' : ''}`}
              title={soundEnabled ? 'Sound alert ON — click to mute' : 'Click to enable sound alert'}
            >
              <Bell size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">{soundEnabled ? 'Sound On' : 'Muted'}</span>
            </Button>
            <Button
              data-testid="close-bookings-btn"
              onClick={() => setBookingsPauseDialogOpen(true)}
              variant="outline"
              size="sm"
              className={`border-white/30 text-white hover:bg-white/10 ${bookingsPause.closed ? 'bg-red-500/60 border-red-300' : (bookingsPause.blocks?.length ? 'bg-amber-500/40 border-amber-300' : '')}`}
              title="Close / reopen online table bookings"
            >
              <Calendar size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">
                {bookingsPause.closed
                  ? 'Bookings Closed'
                  : (bookingsPause.blocks?.length ? `${bookingsPause.blocks.length} Block${bookingsPause.blocks.length > 1 ? 's' : ''}` : 'Close Bookings')}
              </span>
            </Button>
            <Button
              data-testid="ordering-hours-btn"
              onClick={() => fetchHours(true)}
              variant="outline"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
              title="Edit ordering hours"
            >
              <Clock4 size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">Hours</span>
            </Button>
            <Button
              data-testid="close-orders-btn"
              onClick={() => setPauseDialogOpen(true)}
              variant="outline"
              size="sm"
              className={`border-white/30 text-white hover:bg-white/10 ${(orderPause.collection_paused || orderPause.delivery_paused) ? 'bg-red-500/60 border-red-300' : (orderPause.blocks?.length ? 'bg-amber-500/40 border-amber-300' : '')}`}
              title="Pause / resume online ordering"
            >
              <Package size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">
                {orderPause.collection_paused && orderPause.delivery_paused
                  ? 'Orders Closed'
                  : orderPause.collection_paused || orderPause.delivery_paused
                  ? 'Partial'
                  : (orderPause.blocks?.length ? `${orderPause.blocks.length} Block${orderPause.blocks.length > 1 ? 's' : ''}` : 'Close Orders')}
              </span>
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
              <LogOut size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="mb-6 sm:mb-8 flex flex-wrap gap-2">
            <TabsTrigger value="bookings" className="px-4 sm:px-6 py-2 sm:py-3">
              <Calendar size={18} className="mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="floorplan" className="px-4 sm:px-6 py-2 sm:py-3">
              <MapPin size={18} className="mr-2" />
              Floor Plan
            </TabsTrigger>
            <TabsTrigger value="orders" className="px-4 sm:px-6 py-2 sm:py-3">
              <ShoppingBag size={18} className="mr-2" />
              Orders
              {orders.filter(o => o.status === 'pending').length > 0 && (
                <span className="ml-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  {orders.filter(o => o.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="menu" className="px-4 sm:px-6 py-2 sm:py-3" data-testid="admin-tab-menu">
              <BookOpen size={18} className="mr-2" />
              Menu
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6 items-end">
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Date</Label>
                <Input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-40"
                />
              </div>
              {dateFilter && (
                <Button variant="ghost" onClick={() => setDateFilter('')} size="sm">
                  Clear Date
                </Button>
              )}
              <div className="ml-auto">
                <Button
                  data-testid="cleanup-old-bookings-btn"
                  onClick={cleanupOldBookings}
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={14} className="mr-2" />
                  Remove Past Bookings
                </Button>
              </div>
            </div>

            {/* Stats — for selected date range only */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <p className="text-sm text-gray-600">Total{dateFilter ? ` (${dateFilter})` : ''}</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#1B4332]">{dateFilteredBookings.length}</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-200">
                <p className="text-sm text-yellow-700">Pending</p>
                <p className="text-2xl sm:text-3xl font-bold text-yellow-700">
                  {dateFilteredBookings.filter(b => b.status === 'pending').length}
                </p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-200">
                <p className="text-sm text-green-700">Accepted</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-700">
                  {dateFilteredBookings.filter(b => b.status === 'accepted').length}
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 shadow-sm border border-red-200">
                <p className="text-sm text-red-700">Declined</p>
                <p className="text-2xl sm:text-3xl font-bold text-red-700">
                  {dateFilteredBookings.filter(b => b.status === 'declined').length}
                </p>
              </div>
            </div>

            {/* Bookings List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-[#1B4332]" size={32} />
                <p className="mt-2 text-gray-600">Loading bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <Calendar className="mx-auto text-gray-400" size={48} />
                <p className="mt-2 text-gray-600">No bookings found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => (
                  <div key={booking.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border hover:shadow-md transition-shadow">
                    <div className="flex flex-col lg:flex-row gap-4 justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                          <h3 className="font-semibold text-lg text-[#1B4332]">{booking.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                          {booking.table_name && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                              {booking.table_name}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-x-6 text-sm text-gray-600">
                          <p className="flex items-center gap-2">
                            <Calendar size={16} className="text-[#C67C4E]" />
                            <strong>{formatDate(booking.date)}</strong>
                          </p>
                          <p className="flex items-center gap-2">
                            <Clock size={16} className="text-[#C67C4E]" />
                            <strong>{booking.time}</strong>
                          </p>
                          <p className="flex items-center gap-2">
                            <Users size={16} className="text-[#C67C4E]" />
                            {booking.guests} guests
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone size={16} className="text-[#C67C4E]" />
                            <a href={`tel:${booking.phone}`} className="hover:text-[#C67C4E]">{booking.phone}</a>
                          </p>
                          {booking.email && (
                            <p className="flex items-center gap-2">
                              <Mail size={16} className="text-[#C67C4E]" />
                              <a href={`mailto:${booking.email}`} className="hover:text-[#C67C4E]">{booking.email}</a>
                            </p>
                          )}
                        </div>
                        {booking.special_requests && (
                          <p className="mt-2 text-sm text-gray-500 italic bg-gray-50 p-2 rounded">
                            Note: {booking.special_requests}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-row lg:flex-col gap-2 justify-end">
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => updateBookingStatus(booking.id, 'accepted')}
                              className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                              size="sm"
                            >
                              <Check size={16} className="mr-1" />
                              Accept
                            </Button>
                            <Button
                              onClick={() => updateBookingStatus(booking.id, 'declined')}
                              variant="destructive"
                              size="sm"
                            >
                              <X size={16} className="mr-1" />
                              Decline
                            </Button>
                          </>
                        )}
                        <Button
                          data-testid={`edit-booking-${booking.id}`}
                          onClick={() => setEditingBooking({ ...booking })}
                          variant="outline"
                          size="sm"
                          className="border-[#1B4332] text-[#1B4332]"
                        >
                          <Pencil size={16} className="mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Floor Plan Tab */}
          <TabsContent value="floorplan">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B4332]">Restaurant Floor Plan</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Click a booked table to edit the booking.
                    {dateFilter ? ` Showing bookings for ${dateFilter}.` : ' Showing today\'s bookings.'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-44"
                  />
                  {dateFilter && (
                    <Button variant="ghost" size="sm" onClick={() => setDateFilter('')}>Clear</Button>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-600 ml-2">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded-full bg-[#1B4332]"></span> Booked
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-3 rounded-full bg-gray-300 border"></span> Free
                    </span>
                  </div>
                </div>
              </div>
              <div className="relative bg-gray-100 rounded-xl max-w-5xl mx-auto" style={{ overflow: 'visible' }}>
                <img
                  src={FLOOR_PLAN_IMAGE}
                  alt="Restaurant Floor Plan"
                  className="w-full h-auto block rounded-xl"
                />
                {/* Overlay booking pills on each table */}
                {tables.map((table) => {
                  const pos = FLOOR_PLAN_POSITIONS[table.id];
                  if (!pos) return null;
                  const tableBookingsToday = getTableBookingsForDate(table.id, dateFilter);
                  const hasBookings = tableBookingsToday.length > 0;
                  const next = tableBookingsToday[0];
                  return (
                    <button
                      key={table.id}
                      data-testid={`floorplan-marker-${table.id}`}
                      onClick={() => {
                        setTableViewModal({
                          table,
                          bookings: tableBookingsToday,
                          date: dateFilter || new Date().toISOString().slice(0, 10),
                        });
                      }}
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-lg shadow-lg border text-left transition-all hover:scale-110 hover:z-20 z-10 cursor-pointer
                        ${hasBookings
                          ? 'bg-[#1B4332] text-white border-[#0F3025] px-2 py-1.5 min-w-[68px] min-h-[36px]'
                          : 'bg-white/90 text-gray-700 border-gray-300 px-3 py-2 min-w-[52px] min-h-[40px] text-center'}`}
                      title={hasBookings
                        ? `Click to edit ${next.name}'s booking`
                        : `${table.name} — no bookings`}
                    >
                      <div className="text-[10px] font-bold leading-none uppercase tracking-wider opacity-80">
                        {table.name.replace(/^Table\s*/i, 'T')}
                      </div>
                      {hasBookings && next && (
                        <div className="mt-1 leading-tight">
                          <div className="text-[11px] font-semibold truncate max-w-[120px]">
                            {next.name}
                          </div>
                          <div className="text-[10px] opacity-90">
                            {next.time} · {next.guests}p
                          </div>
                          {tableBookingsToday.length > 1 && (
                            <div className="text-[9px] opacity-75 mt-0.5">
                              +{tableBookingsToday.length - 1} more
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Tables tab content removed from UI; dialog moved to top level */}
          <TabsContent value="tables">
            <div className="text-center py-12 text-gray-500">
              Tables can be managed from the Floor Plan — click any table and "Edit Table".
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="flex flex-wrap gap-4 mb-6 items-end">
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Status</Label>
                <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Orders</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="preparing">Preparing</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchOrders} variant="outline" size="sm">
                <RefreshCw size={16} className="mr-2" /> Refresh
              </Button>
              <Button
                data-testid="orders-today-toggle"
                onClick={() => setShowTodayOrdersOnly(v => !v)}
                variant={showTodayOrdersOnly ? 'default' : 'outline'}
                size="sm"
                className={showTodayOrdersOnly ? 'bg-[#1B4332] hover:bg-[#2D6A4F] text-white' : ''}
                title={showTodayOrdersOnly ? "Showing today's orders only — click to show all" : "Showing all orders — click to show today's only"}
              >
                <Calendar size={16} className="mr-2" />
                {showTodayOrdersOnly ? "Today's Orders" : 'All Orders'}
              </Button>
            </div>

            {/* Order Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
              <div className="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-200">
                <p className="text-sm text-yellow-700">Pending</p>
                <p className="text-2xl font-bold text-yellow-700">{orders.filter(o => o.status === 'pending').length}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-200">
                <p className="text-sm text-blue-700">Confirmed</p>
                <p className="text-2xl font-bold text-blue-700">{orders.filter(o => o.status === 'confirmed').length}</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 shadow-sm border border-orange-200">
                <p className="text-sm text-orange-700">Preparing</p>
                <p className="text-2xl font-bold text-orange-700">{orders.filter(o => o.status === 'preparing').length}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-200">
                <p className="text-sm text-green-700">Ready</p>
                <p className="text-2xl font-bold text-green-700">{orders.filter(o => o.status === 'ready').length}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 shadow-sm border border-gray-200">
                <p className="text-sm text-gray-700">Total</p>
                <p className="text-2xl font-bold text-gray-700">{orders.length}</p>
              </div>
            </div>

            {ordersLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-[#1B4332]" size={32} />
                <p className="mt-2 text-gray-600">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <ShoppingBag className="mx-auto text-gray-400" size={48} />
                <p className="mt-2 text-gray-600">No orders found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <div key={order.id} data-testid={`admin-order-${order.id}`} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border hover:shadow-md transition-shadow">
                    <div className="flex flex-col lg:flex-row gap-4 justify-between">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <h3 className="font-semibold text-lg text-[#1B4332]">{order.customer_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getOrderStatusColor(order.status)}`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`}>
                            {order.payment_status === 'pay_in_store' ? 'Pay in Store' : order.payment_status === 'paid' ? 'Paid Online' : 'Unpaid'}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {order.order_type === 'collection' ? 'Collection' : 'Delivery'}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                          <p className="flex items-center gap-2">
                            <Mail size={16} className="text-[#C67C4E]" />
                            <a href={`mailto:${order.customer_email}`} className="hover:text-[#C67C4E]">{order.customer_email}</a>
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone size={16} className="text-[#C67C4E]" />
                            <a href={`tel:${order.customer_phone}`} className="hover:text-[#C67C4E]">{order.customer_phone}</a>
                          </p>
                          <p className="flex items-center gap-2">
                            <Clock size={16} className="text-[#C67C4E]" />
                            {new Date(order.created_at).toLocaleString('en-GB')}
                          </p>
                          <p className="flex items-center gap-2">
                            <Package size={16} className="text-[#C67C4E]" />
                            <strong>{"\u00A3"}{order.total?.toFixed(2)}</strong>
                          </p>
                        </div>

                        {/* Order Items */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Items</p>
                          <div className="space-y-1">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{item.quantity}x {item.name}</span>
                                <span className="text-gray-600">{"\u00A3"}{item.line_total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {order.special_instructions && (
                          <p className="mt-2 text-sm text-gray-500 italic bg-yellow-50 p-2 rounded">
                            Note: {order.special_instructions}
                          </p>
                        )}
                        {order.order_type === 'delivery' && order.delivery_address && (
                          <div className="mt-2 text-sm bg-purple-50 p-3 rounded-lg border border-purple-100">
                            <p className="font-medium text-purple-800 flex items-center gap-1 mb-1">
                              <MapPin size={14} /> Delivery Address
                            </p>
                            <p className="text-purple-700">{order.delivery_address}</p>
                            {order.delivery_postcode && (
                              <p className="text-purple-700 font-medium">{order.delivery_postcode}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Order Actions */}
                      <div className="flex flex-col gap-2 justify-start flex-shrink-0 min-w-[140px]">
                        {order.status === 'pending' && (
                          <>
                            <Button
                              data-testid={`accept-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'confirmed')}
                              className="bg-[#1B4332] hover:bg-[#2D6A4F] h-11 text-base"
                            >
                              <Check size={18} className="mr-2" /> Accept
                            </Button>
                            <Button
                              onClick={() => updateOrderStatus(order.id, 'cancelled')}
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50 h-9"
                              size="sm"
                            >
                              <X size={14} className="mr-1" /> Cancel
                            </Button>
                          </>
                        )}
                        {order.status === 'confirmed' && (
                          <>
                            <Button
                              data-testid={`preparing-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'preparing')}
                              className="bg-orange-500 hover:bg-orange-600 text-white h-11 text-base"
                            >
                              Preparing
                            </Button>
                            <Button
                              data-testid={`undo-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'pending')}
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs text-gray-600"
                              title="Move back to Pending"
                            >
                              ↶ Undo
                            </Button>
                          </>
                        )}
                        {order.status === 'preparing' && (
                          <>
                            <Button
                              data-testid={`ready-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'ready')}
                              className="bg-green-600 hover:bg-green-700 text-white h-11 text-base"
                            >
                              <Check size={18} className="mr-2" /> Ready
                            </Button>
                            <Button
                              data-testid={`undo-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'confirmed')}
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs text-gray-600"
                              title="Move back to Confirmed"
                            >
                              ↶ Undo
                            </Button>
                          </>
                        )}
                        {order.status === 'ready' && (
                          <>
                            <Button
                              data-testid={`complete-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'completed')}
                              className="bg-gray-700 hover:bg-gray-800 text-white h-10"
                            >
                              <Check size={16} className="mr-2" /> Complete
                            </Button>
                            <Button
                              data-testid={`undo-order-${order.id}`}
                              onClick={() => updateOrderStatus(order.id, 'preparing')}
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs text-gray-600"
                              title="Move back to Preparing"
                            >
                              ↶ Undo
                            </Button>
                          </>
                        )}
                        {order.status === 'completed' && (
                          <Button
                            data-testid={`undo-order-${order.id}`}
                            onClick={() => updateOrderStatus(order.id, 'ready')}
                            variant="outline"
                            size="sm"
                            className="h-9 text-gray-600"
                            title="Re-open this order"
                          >
                            ↶ Undo Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Menu Tab — toggle items in/out of stock */}
          <TabsContent value="menu">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1B4332]">Menu Stock</h2>
                <p className="text-sm text-[#636E72] mt-1">
                  Toggle any item to <strong className="text-red-600">Sold Out</strong> to instantly stop online + app orders for it. Switch back when you have it again.
                </p>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Search</Label>
                  <Input
                    data-testid="menu-search-input"
                    placeholder="Search items…"
                    value={menuSearch}
                    onChange={(e) => setMenuSearch(e.target.value)}
                    className="w-56"
                  />
                </div>
                <Button onClick={fetchMenuStock} variant="outline" size="sm" data-testid="menu-refresh-btn">
                  <RefreshCw size={14} className="mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {menuStockLoading ? (
              <div className="text-center py-10 text-[#636E72]">Loading menu…</div>
            ) : (
              <div className="space-y-8">
                {Object.entries(fullMenu).map(([catKey, items]) => {
                  if (!items || items.length === 0) return null;
                  const filtered = items.filter(it =>
                    !menuSearch || it.name.toLowerCase().includes(menuSearch.toLowerCase())
                  );
                  if (filtered.length === 0) return null;
                  const soldOutCount = items.filter(it => menuStock[`${catKey}::${it.name}`] === false).length;
                  return (
                    <div key={catKey} data-testid={`menu-category-${catKey}`} className="bg-white rounded-2xl border border-[#1B4332]/10 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-4 bg-[#1B4332] text-white">
                        <h3 className="font-serif text-lg sm:text-xl font-semibold">
                          {MENU_CATEGORY_LABELS[catKey] || catKey}
                        </h3>
                        <span className="text-xs sm:text-sm text-white/80">
                          {items.length} items
                          {soldOutCount > 0 && (
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold">
                              {soldOutCount} sold out
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {filtered.map((item) => {
                          const key = `${catKey}::${item.name}`;
                          const stockValue = menuStock[key];
                          const inStock = stockValue !== false; // default: in stock
                          return (
                            <div
                              key={item.name}
                              data-testid={`menu-stock-row-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                              className={`flex items-center justify-between gap-4 px-5 py-3 ${inStock ? '' : 'bg-red-50'}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-[#1B4332]">{item.name}</p>
                                  {!inStock && (
                                    <span className="text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full uppercase tracking-wide">
                                      Sold Out
                                    </span>
                                  )}
                                  {item.vegetarian && (
                                    <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">V</span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="text-xs text-[#636E72] mt-0.5 line-clamp-1">{item.description}</p>
                                )}
                              </div>
                              <span className="text-sm font-semibold text-[#C67C4E] whitespace-nowrap hidden sm:inline">
                                {item.price}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-medium ${inStock ? 'text-green-700' : 'text-red-700'}`}>
                                  {inStock ? 'In Stock' : 'Out of Stock'}
                                </span>
                                <Switch
                                  checked={inStock}
                                  onCheckedChange={() => toggleMenuStock(catKey, item.name, inStock)}
                                  data-testid={`menu-stock-switch-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Table Dialog (lives at top level so it works from Floor Plan markers) */}
      <Dialog open={!!editingTable} onOpenChange={(open) => !open && setEditingTable(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Table</DialogTitle>
            <DialogDescription>Update the details for this table.</DialogDescription>
          </DialogHeader>
          {editingTable && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Table Name</Label>
                <Input
                  data-testid="edit-table-name-input"
                  value={editingTable.name}
                  onChange={(e) => setEditingTable({ ...editingTable, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Capacity (seats)</Label>
                <Input
                  data-testid="edit-table-capacity-input"
                  type="number"
                  value={editingTable.capacity}
                  onChange={(e) => setEditingTable({ ...editingTable, capacity: e.target.value })}
                />
              </div>
              <div>
                <Label>Location (optional)</Label>
                <Input
                  data-testid="edit-table-location-input"
                  placeholder="e.g., Window, Patio, Main Hall"
                  value={editingTable.location || ''}
                  onChange={(e) => setEditingTable({ ...editingTable, location: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTable(null)}>Cancel</Button>
            <Button data-testid="save-table-btn" onClick={updateTable} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table View Dialog (shown when a floor plan marker is clicked) */}
      <Dialog open={!!tableViewModal} onOpenChange={(open) => !open && setTableViewModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{tableViewModal?.table?.name}</DialogTitle>
            <DialogDescription>
              {tableViewModal?.table?.capacity}-seater{tableViewModal?.table?.location ? ` · ${tableViewModal.table.location}` : ''}
              {tableViewModal?.date ? ` · Bookings for ${tableViewModal.date}` : ''}
            </DialogDescription>
          </DialogHeader>
          {tableViewModal && (
            <div className="space-y-3 py-2 max-h-[55vh] overflow-y-auto pr-1">
              {tableViewModal.bookings.length === 0 ? (
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                  <Calendar className="mx-auto text-gray-400 mb-2" size={28} />
                  <p className="text-sm">No bookings on this table for the selected date.</p>
                </div>
              ) : (
                tableViewModal.bookings.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`table-view-booking-${b.id}`}
                    onClick={() => {
                      setTableViewModal(null);
                      setEditingBooking({ ...b });
                    }}
                    className="w-full text-left bg-white border rounded-lg p-3 hover:border-[#1B4332] hover:shadow-sm transition-all"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-[#1B4332]">{b.name}</p>
                        <p className="text-xs text-gray-500">
                          {b.time} · {b.guests} guests
                        </p>
                        {b.phone && (
                          <p className="text-xs text-gray-500 mt-1">📞 {b.phone}</p>
                        )}
                        {b.special_requests && (
                          <p className="text-xs text-gray-500 mt-1 italic">"{b.special_requests}"</p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(b.status)}`}>
                        {b.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <Button
              data-testid="table-view-edit-table-btn"
              variant="outline"
              onClick={() => {
                const tbl = tableViewModal.table;
                setTableViewModal(null);
                setEditingTable({ ...tbl });
              }}
              className="sm:mr-auto"
            >
              <Pencil size={14} className="mr-2" />
              Edit Table
            </Button>
            <Button
              variant="outline"
              onClick={() => setTableViewModal(null)}
            >
              Close
            </Button>
            <Button
              data-testid="table-view-add-booking-btn"
              onClick={() => {
                const tbl = tableViewModal.table;
                setNewBooking({
                  name: '',
                  email: '',
                  phone: '',
                  date: tableViewModal.date || '',
                  time: '',
                  guests: '',
                  special_requests: '',
                  table_id: tbl.id,
                  table_ids: [tbl.id],
                });
                setTableViewModal(null);
                setIsAddBookingOpen(true);
              }}
              className="bg-[#1B4332] hover:bg-[#2D6A4F]"
            >
              <Plus size={16} className="mr-2" />
              Add Booking on this Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close / Reopen Table Bookings Dialog */}
      <Dialog open={bookingsPauseDialogOpen} onOpenChange={setBookingsPauseDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Close Online Table Bookings</DialogTitle>
            <DialogDescription>
              Close all bookings, or block specific date / time windows (e.g. a private event). Changes go live instantly on the website.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Master switch */}
            <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${bookingsPause.closed ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
              <div>
                <p className="font-semibold text-[#1B4332]">Close ALL online bookings</p>
                <p className="text-xs text-gray-500">When ON, no time slots will be shown on /booking, regardless of any scheduled blocks below.</p>
              </div>
              <input
                type="checkbox"
                data-testid="bookings-pause-toggle"
                checked={bookingsPause.closed}
                onChange={(e) => setBookingsPause({ ...bookingsPause, closed: e.target.checked })}
                className="w-5 h-5 accent-red-600"
              />
            </label>
            <div>
              <Label className="text-xs">Optional message to customers (shown when fully closed)</Label>
              <Input
                placeholder="e.g., Fully booked tonight — please call us"
                value={bookingsPause.message}
                onChange={(e) => setBookingsPause({ ...bookingsPause, message: e.target.value })}
                data-testid="bookings-pause-message"
              />
            </div>

            {/* Scheduled Blocks */}
            <div className="border-t pt-4">
              <div className="mb-3">
                <p className="font-semibold text-[#1B4332]">Block specific date / time windows</p>
                <p className="text-xs text-gray-500">Each block stops customers from booking ONLY during that window — useful for private events, kitchen close-downs, etc.</p>
              </div>

              {/* Existing blocks list */}
              {bookingsPause.blocks?.length > 0 && (
                <div className="space-y-2 mb-4">
                  {bookingsPause.blocks.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`booking-block-${b.id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1B4332]">
                          {new Date(b.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-[#636E72]">
                          {b.start} – {b.end}{b.reason ? ` · ${b.reason}` : ''}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBlock(b.id)}
                        data-testid={`booking-block-remove-${b.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new block */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={newBlock.date}
                    onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                    data-testid="new-block-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input
                    type="time"
                    value={newBlock.start}
                    onChange={(e) => setNewBlock({ ...newBlock, start: e.target.value })}
                    data-testid="new-block-start"
                  />
                </div>
                <div>
                  <Label className="text-xs">Until</Label>
                  <Input
                    type="time"
                    value={newBlock.end}
                    onChange={(e) => setNewBlock({ ...newBlock, end: e.target.value })}
                    data-testid="new-block-end"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs">Reason (optional)</Label>
                  <Input
                    placeholder="Private event"
                    value={newBlock.reason}
                    onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                    data-testid="new-block-reason"
                  />
                </div>
                <Button
                  onClick={addBlockToBookings}
                  className="bg-[#C67C4E] hover:bg-[#B36C40] text-white col-span-2 sm:col-span-1"
                  data-testid="add-block-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add Block
                </Button>
              </div>
              {newBlock.date && (
                <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <strong>Tip:</strong> Click <em>Add Block</em> to save this entry. Or just click <em>Save &amp; Apply</em> — we&apos;ll add it for you.
                </p>
              )}

              {/* Bulk range block */}
              <div className="mt-5 pt-4 border-t">
                <p className="text-sm font-semibold text-[#1B4332] mb-2">Or block a whole date range in one click</p>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                  <div>
                    <Label className="text-xs">From date</Label>
                    <Input type="date" value={bulkRange.from} onChange={(e) => setBulkRange({ ...bulkRange, from: e.target.value })} data-testid="bulk-block-from" />
                  </div>
                  <div>
                    <Label className="text-xs">To date</Label>
                    <Input type="date" value={bulkRange.to} onChange={(e) => setBulkRange({ ...bulkRange, to: e.target.value })} data-testid="bulk-block-to" />
                  </div>
                  <div>
                    <Label className="text-xs">From time</Label>
                    <Input type="time" value={bulkRange.start} onChange={(e) => setBulkRange({ ...bulkRange, start: e.target.value })} data-testid="bulk-block-start" />
                  </div>
                  <div>
                    <Label className="text-xs">Until time</Label>
                    <Input type="time" value={bulkRange.end} onChange={(e) => setBulkRange({ ...bulkRange, end: e.target.value })} data-testid="bulk-block-end" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-xs">Reason</Label>
                    <Input placeholder="Closed" value={bulkRange.reason} onChange={(e) => setBulkRange({ ...bulkRange, reason: e.target.value })} data-testid="bulk-block-reason" />
                  </div>
                  <Button
                    onClick={async () => {
                      await addBookingBulkRange(bulkRange.from, bulkRange.to, bulkRange.start, bulkRange.end, bulkRange.reason);
                      setBulkRange({ from: '', to: '', start: '12:00', end: '17:00', reason: '' });
                    }}
                    className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white col-span-2 sm:col-span-1"
                    data-testid="bulk-block-add-bookings-btn"
                  >
                    Block Range
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Uses 00:00–23:59 for &quot;all day&quot;. Duplicates are automatically skipped.</p>
              </div>
            </div>

            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => {
                  saveBookingsPause({ closed: false, message: '', blocks: [] });
                  setBookingsPauseDialogOpen(false);
                }}
                data-testid="reopen-bookings-btn"
              >
                Reopen All & Clear Blocks
              </Button>
              <Button
                className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
                onClick={async () => {
                  // Auto-include any un-added block-form entry so users don't lose it
                  let toSave = bookingsPause;
                  if (newBlock.date && newBlock.start && newBlock.end && newBlock.start < newBlock.end) {
                    toSave = { ...bookingsPause, blocks: [...bookingsPause.blocks, newBlock] };
                    setNewBlock({ date: '', start: '18:00', end: '22:00', reason: '' });
                  }
                  await saveBookingsPause(toSave);
                  setBookingsPauseDialogOpen(false);
                }}
                data-testid="save-bookings-pause-btn"
              >
                Save & Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ordering Hours Editor Dialog */}
      <Dialog open={hoursDialogOpen} onOpenChange={setHoursDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ordering Hours</DialogTitle>
            <DialogDescription>
              Set Collection &amp; Delivery hours per day. Changes go live instantly on the website &amp; app. Tick "Closed" to disable a day entirely. Click <strong>Reset</strong> to revert to the standard hours.
            </DialogDescription>
          </DialogHeader>

          {!hoursDraft ? (
            <div className="py-8 text-center text-[#636E72]">Loading hours…</div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="hidden md:grid grid-cols-[80px_1fr_1fr] gap-3 text-xs uppercase tracking-wide text-[#636E72] font-semibold px-2">
                <div>Day</div>
                <div>Collection</div>
                <div>Delivery</div>
              </div>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((dayLabel, dayIdx) => {
                const renderRow = (kind) => {
                  const entry = hoursDraft[kind][dayIdx];
                  const closed = entry === null;
                  const setEntry = (newEntry) => {
                    setHoursDraft(prev => {
                      const arr = [...prev[kind]];
                      arr[dayIdx] = newEntry;
                      return { ...prev, [kind]: arr };
                    });
                  };
                  const toHHMM = (h, m) => `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
                  const parseHHMM = (s) => {
                    const [h, m] = s.split(':').map(n => parseInt(n, 10));
                    return [isNaN(h) ? 0 : h, isNaN(m) ? 0 : m];
                  };
                  return (
                    <div className={`flex items-center gap-2 ${closed ? 'opacity-60' : ''}`}>
                      <Input
                        type="time"
                        disabled={closed}
                        value={closed ? '00:00' : toHHMM(entry[0], entry[1])}
                        onChange={(e) => {
                          const [h, m] = parseHHMM(e.target.value);
                          setEntry([h, m, entry?.[2] ?? 21, entry?.[3] ?? 0]);
                        }}
                        className="w-28"
                        data-testid={`hours-${kind}-${dayLabel.toLowerCase()}-open`}
                      />
                      <span className="text-[#636E72]">–</span>
                      <Input
                        type="time"
                        disabled={closed}
                        value={closed ? '00:00' : toHHMM(entry[2], entry[3])}
                        onChange={(e) => {
                          const [h, m] = parseHHMM(e.target.value);
                          setEntry([entry?.[0] ?? 12, entry?.[1] ?? 0, h, m]);
                        }}
                        className="w-28"
                        data-testid={`hours-${kind}-${dayLabel.toLowerCase()}-close`}
                      />
                      <label className="flex items-center gap-1 text-xs text-[#636E72] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={closed}
                          data-testid={`hours-${kind}-${dayLabel.toLowerCase()}-closed`}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEntry(null);
                            } else {
                              // Restore from defaults when unchecking
                              const def = hoursDefaults?.[kind]?.[dayIdx];
                              setEntry(def ? [...def] : [kind === 'delivery' ? 17 : 12, 0, kind === 'delivery' ? 21 : 21, 0]);
                            }
                          }}
                          className="w-4 h-4 accent-red-600"
                        />
                        Closed
                      </label>
                    </div>
                  );
                };
                return (
                  <div key={dayLabel} className="grid grid-cols-1 md:grid-cols-[80px_1fr_1fr] gap-3 items-center bg-white p-3 rounded-lg border border-gray-100">
                    <div className="font-semibold text-[#1B4332]">{dayLabel}</div>
                    <div>{renderRow('collection')}</div>
                    <div>{renderRow('delivery')}</div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={resetHoursToDefaults}
              disabled={hoursSaving || !hoursDraft}
              data-testid="hours-reset-btn"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              Reset to Standard Hours
            </Button>
            <Button
              variant="outline"
              onClick={() => setHoursDialogOpen(false)}
              disabled={hoursSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={saveHours}
              disabled={hoursSaving || !hoursDraft}
              className="bg-[#1B4332] hover:bg-[#2D6A4F]"
              data-testid="hours-save-btn"
            >
              {hoursSaving ? 'Saving…' : 'Save & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close / Pause Orders Dialog */}
      <Dialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pause Online Ordering</DialogTitle>
            <DialogDescription>
              Pause all ordering now, or block specific date/time windows (e.g. closed for a holiday period). Changes apply instantly on the website &amp; app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${orderPause.collection_paused ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
              <div>
                <p className="font-semibold text-[#1B4332]">Collection orders</p>
                <p className="text-xs text-gray-500">Customers can't place collection orders while paused</p>
              </div>
              <input
                type="checkbox"
                data-testid="pause-collection-toggle"
                checked={orderPause.collection_paused}
                onChange={(e) => setOrderPause({ ...orderPause, collection_paused: e.target.checked })}
                className="w-5 h-5 accent-red-600"
              />
            </label>
            <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${orderPause.delivery_paused ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
              <div>
                <p className="font-semibold text-[#1B4332]">Delivery orders</p>
                <p className="text-xs text-gray-500">Customers can't place delivery orders while paused</p>
              </div>
              <input
                type="checkbox"
                data-testid="pause-delivery-toggle"
                checked={orderPause.delivery_paused}
                onChange={(e) => setOrderPause({ ...orderPause, delivery_paused: e.target.checked })}
                className="w-5 h-5 accent-red-600"
              />
            </label>
            <div>
              <Label className="text-xs">Optional message to customers</Label>
              <Input
                placeholder="e.g., We're closing early today. Sorry for any inconvenience!"
                value={orderPause.pause_message}
                onChange={(e) => setOrderPause({ ...orderPause, pause_message: e.target.value })}
              />
            </div>

            {/* Date/time blocks */}
            <div className="border-t pt-4">
              <div className="mb-3">
                <p className="font-semibold text-[#1B4332]">Block specific date / time windows</p>
                <p className="text-xs text-gray-500">Each block stops customers from ordering ONLY during that date/time window. Great for holidays, private events, etc.</p>
              </div>

              {orderPause.blocks?.length > 0 && (
                <div className="space-y-2 mb-4 max-h-56 overflow-y-auto">
                  {[...orderPause.blocks]
                    .sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start))
                    .map((b) => (
                      <div
                        key={b.id}
                        data-testid={`order-block-${b.id}`}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1B4332]">
                            {new Date(b.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          <p className="text-xs text-[#636E72]">
                            {b.start} – {b.end}{b.reason ? ` · ${b.reason}` : ''}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOrderBlock(b.id)}
                          data-testid={`order-block-remove-${b.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={newOrderBlock.date}
                    onChange={(e) => setNewOrderBlock({ ...newOrderBlock, date: e.target.value })}
                    data-testid="new-order-block-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input
                    type="time"
                    value={newOrderBlock.start}
                    onChange={(e) => setNewOrderBlock({ ...newOrderBlock, start: e.target.value })}
                    data-testid="new-order-block-start"
                  />
                </div>
                <div>
                  <Label className="text-xs">Until</Label>
                  <Input
                    type="time"
                    value={newOrderBlock.end}
                    onChange={(e) => setNewOrderBlock({ ...newOrderBlock, end: e.target.value })}
                    data-testid="new-order-block-end"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs">Reason (optional)</Label>
                  <Input
                    placeholder="Restaurant closed"
                    value={newOrderBlock.reason}
                    onChange={(e) => setNewOrderBlock({ ...newOrderBlock, reason: e.target.value })}
                    data-testid="new-order-block-reason"
                  />
                </div>
                <Button
                  onClick={addOrderBlock}
                  className="bg-[#C67C4E] hover:bg-[#B36C40] text-white col-span-2 sm:col-span-1"
                  data-testid="add-order-block-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add Block
                </Button>
              </div>
              {newOrderBlock.date && (
                <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <strong>Tip:</strong> Click <em>Add Block</em> to save this entry. Or just click <em>Save &amp; Apply</em> — we&apos;ll add it for you.
                </p>
              )}

              {/* Bulk range block for orders */}
              <div className="mt-5 pt-4 border-t">
                <p className="text-sm font-semibold text-[#1B4332] mb-2">Or block a whole date range in one click</p>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                  <div>
                    <Label className="text-xs">From date</Label>
                    <Input type="date" value={bulkRange.from} onChange={(e) => setBulkRange({ ...bulkRange, from: e.target.value })} data-testid="bulk-order-block-from" />
                  </div>
                  <div>
                    <Label className="text-xs">To date</Label>
                    <Input type="date" value={bulkRange.to} onChange={(e) => setBulkRange({ ...bulkRange, to: e.target.value })} data-testid="bulk-order-block-to" />
                  </div>
                  <div>
                    <Label className="text-xs">From time</Label>
                    <Input type="time" value={bulkRange.start} onChange={(e) => setBulkRange({ ...bulkRange, start: e.target.value })} data-testid="bulk-order-block-start" />
                  </div>
                  <div>
                    <Label className="text-xs">Until time</Label>
                    <Input type="time" value={bulkRange.end} onChange={(e) => setBulkRange({ ...bulkRange, end: e.target.value })} data-testid="bulk-order-block-end" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-xs">Reason</Label>
                    <Input placeholder="Closed" value={bulkRange.reason} onChange={(e) => setBulkRange({ ...bulkRange, reason: e.target.value })} data-testid="bulk-order-block-reason" />
                  </div>
                  <Button
                    onClick={async () => {
                      await addOrderBulkRange(bulkRange.from, bulkRange.to, bulkRange.start, bulkRange.end, bulkRange.reason);
                      setBulkRange({ from: '', to: '', start: '12:00', end: '17:00', reason: '' });
                    }}
                    className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white col-span-2 sm:col-span-1"
                    data-testid="bulk-block-add-orders-btn"
                  >
                    Block Range
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Uses 00:00–23:59 for &quot;all day&quot;. Duplicates are automatically skipped.</p>
              </div>
            </div>

            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => {
                  const next = { collection_paused: false, delivery_paused: false, pause_message: '', blocks: [] };
                  saveOrderPause(next);
                  setPauseDialogOpen(false);
                }}
                data-testid="resume-all-btn"
              >
                Resume & Clear All Blocks
              </Button>
              <Button
                className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
                onClick={async () => {
                  // Auto-include any un-added block-form entry so users don't lose it
                  let toSave = orderPause;
                  if (newOrderBlock.date && newOrderBlock.start && newOrderBlock.end && newOrderBlock.start < newOrderBlock.end) {
                    toSave = { ...orderPause, blocks: [...orderPause.blocks, newOrderBlock] };
                    setNewOrderBlock({ date: '', start: '12:00', end: '17:00', reason: '' });
                  }
                  await saveOrderPause(toSave);
                  setPauseDialogOpen(false);
                }}
                data-testid="save-pause-btn"
              >
                Save &amp; Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog (works from any tab — Bookings list AND Floor Plan markers) */}
      <Dialog open={!!editingBooking} onOpenChange={(open) => !open && setEditingBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
            <DialogDescription>
              Update booking details, change the table, or delete it.
            </DialogDescription>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  data-testid="edit-booking-name"
                  value={editingBooking.name || ''}
                  onChange={(e) => setEditingBooking({ ...editingBooking, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date *</Label>
                  <Input
                    data-testid="edit-booking-date"
                    type="date"
                    value={editingBooking.date || ''}
                    onChange={(e) => setEditingBooking({ ...editingBooking, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input
                    data-testid="edit-booking-time"
                    type="time"
                    value={editingBooking.time || ''}
                    onChange={(e) => setEditingBooking({ ...editingBooking, time: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Guests *</Label>
                  <Input
                    data-testid="edit-booking-guests"
                    type="number"
                    min="1"
                    value={editingBooking.guests || ''}
                    onChange={(e) => setEditingBooking({ ...editingBooking, guests: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editingBooking.status || 'accepted'}
                    onValueChange={(val) => setEditingBooking({ ...editingBooking, status: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Tables (tick one or more)</Label>
                <p className="text-xs text-gray-500 mb-2">For larger parties tick multiple tables to combine seats.</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto border rounded-lg p-2">
                  {tables.map((table) => {
                    const ids = editingBooking.table_ids || (editingBooking.combined_table_ids?.length ? editingBooking.combined_table_ids : (editingBooking.table_id ? [editingBooking.table_id] : []));
                    const checked = ids.includes(table.id);
                    return (
                      <label
                        key={table.id}
                        className={`flex items-center gap-1 px-2 py-1.5 rounded text-sm cursor-pointer transition-colors ${checked ? 'bg-[#1B4332] text-white' : 'bg-white hover:bg-gray-50 border'}`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...ids, table.id]
                              : ids.filter(id => id !== table.id);
                            setEditingBooking({ ...editingBooking, table_ids: next });
                          }}
                        />
                        <span className="font-semibold">{table.name.replace(/^Table\s*/i, 'T')}</span>
                        <span className="text-xs opacity-80">{table.capacity}p</span>
                      </label>
                    );
                  })}
                </div>
                {((editingBooking.table_ids?.length || (editingBooking.combined_table_ids?.length || 0)) > 1) && (
                  <p className="text-xs text-[#1B4332] mt-2 font-medium">
                    Combined: {(editingBooking.table_ids || editingBooking.combined_table_ids || []).reduce((s, id) => s + (tables.find(t => t.id === id)?.capacity || 0), 0)} seats
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editingBooking.phone || ''}
                    onChange={(e) => setEditingBooking({ ...editingBooking, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingBooking.email || ''}
                    onChange={(e) => setEditingBooking({ ...editingBooking, email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={editingBooking.special_requests || ''}
                  onChange={(e) => setEditingBooking({ ...editingBooking, special_requests: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <Button
              data-testid="delete-booking-btn"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 sm:mr-auto"
              onClick={() => editingBooking && deleteBooking(editingBooking.id)}
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => setEditingBooking(null)}>Cancel</Button>
            <Button data-testid="save-booking-btn" onClick={saveBookingEdit} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Table Dialog */}
      <Dialog open={isAssignTableOpen} onOpenChange={setIsAssignTableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Table</DialogTitle>
            <DialogDescription>
              {selectedBooking && (
                <span>
                  Assign a table for {selectedBooking.name}&apos;s booking on {formatDate(selectedBooking.date)} at {selectedBooking.time} ({selectedBooking.guests} guests)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {tables.length === 0 ? (
              <p className="text-center text-gray-600">No tables available. Please create tables first.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {tables.map((table) => {
                  const isCurrentTable = selectedBooking?.table_id === table.id;
                  const tableBookingsOnDate = selectedBooking ? 
                    bookings.filter(b => b.table_id === table.id && b.date === selectedBooking.date && b.status === 'accepted' && b.id !== selectedBooking.id) : [];
                  
                  return (
                    <button
                      key={table.id}
                      onClick={() => assignTable(selectedBooking?.id, table.id)}
                      className={`w-full p-4 rounded-lg border text-left hover:border-[#1B4332] hover:bg-[#1B4332]/5 transition-colors ${
                        isCurrentTable ? 'border-[#1B4332] bg-[#1B4332]/10' : ''
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-[#1B4332]">{table.name}</p>
                          <p className="text-sm text-gray-600">
                            {table.capacity} seats {table.location && `• ${table.location}`}
                          </p>
                          {tableBookingsOnDate.length > 0 && (
                            <p className="text-xs text-orange-600 mt-1">
                              {tableBookingsOnDate.length} other booking(s) on this date
                            </p>
                          )}
                        </div>
                        {isCurrentTable && (
                          <Check size={20} className="text-[#1B4332]" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Table Details Dialog */}
      <Dialog open={!!selectedTableForView} onOpenChange={() => setSelectedTableForView(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTableForView?.name} - Bookings</DialogTitle>
            <DialogDescription>
              {selectedTableForView?.capacity} seats • {selectedTableForView?.location || 'No location set'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {tableBookings.length === 0 ? (
              <p className="text-center text-gray-600 py-8">No bookings for this table</p>
            ) : (
              <div className="space-y-3">
                {tableBookings.map((booking) => (
                  <div key={booking.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-[#1B4332]">{booking.name}</p>
                        <p className="text-sm text-gray-600">{booking.guests} guests</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-[#C67C4E]">{booking.time}</p>
                        <p className="text-sm text-gray-600">{formatDate(booking.date)}</p>
                      </div>
                    </div>
                    {booking.special_requests && (
                      <p className="text-sm text-gray-500 mt-2 italic">{booking.special_requests}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Order Alert Modal */}
      {newOrderAlert && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div data-testid="new-order-alert" className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-bounceIn">
            <div className="bg-[#1B4332] p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Bell size={32} className="text-white animate-ring" />
              </div>
              <h2 className="text-white text-2xl font-bold">
                New {newOrderAlert.order_type === 'delivery' ? 'Delivery' : 'Collection'} Order!
              </h2>
            </div>
            <div className="p-6">
              <div className="text-center mb-5">
                <p className="text-3xl font-bold text-[#C67C4E]">{"\u00A3"}{newOrderAlert.total?.toFixed(2)}</p>
                <p className="text-lg text-[#1B4332] font-semibold mt-1">{newOrderAlert.customer_name}</p>
                <p className="text-sm text-[#636E72]">{newOrderAlert.customer_phone}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Items</p>
                {newOrderAlert.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>{item.quantity}x {item.name}</span>
                    <span className="text-gray-600">{"\u00A3"}{item.line_total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {newOrderAlert.order_type === 'delivery' && newOrderAlert.delivery_address && (
                <div className="bg-purple-50 rounded-xl p-4 mb-5 border border-purple-100">
                  <p className="text-xs font-medium text-purple-500 uppercase mb-1">Delivery Address</p>
                  <p className="text-sm text-purple-800">{newOrderAlert.delivery_address}</p>
                  <p className="text-sm font-semibold text-purple-800">{newOrderAlert.delivery_postcode}</p>
                </div>
              )}
              {newOrderAlert.special_instructions && (
                <p className="text-sm text-gray-500 italic mb-5 bg-yellow-50 p-3 rounded-lg">Note: {newOrderAlert.special_instructions}</p>
              )}
              <div className="flex gap-3">
                <Button
                  data-testid="alert-view-orders-btn"
                  onClick={() => {
                    setNewOrderAlert(null);
                    document.querySelector('[value="orders"]')?.click();
                  }}
                  className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F] h-12 text-base"
                >
                  View Orders
                </Button>
                <Button
                  data-testid="alert-dismiss-btn"
                  onClick={() => setNewOrderAlert(null)}
                  variant="outline"
                  className="flex-1 h-12 text-base"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
